import React, { useEffect, useState } from 'react';
import './Styles/Login.css';
import '../constants/styleConstants.css';
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../Components/LoadingSpinner";
import { GeneralPopupComponent, PopupType } from "../Components/GeneralPopupComponent";
import sanctum_axios_client from '../services/sanctum_axios_client';
import auth_axios_client from '../services/auth_axios_client';
import { LOCAL_STORAGE_KEYS } from '../constants/Constants';

const Login: React.FC = () => {
  const navigate = useNavigate();

  // This effect handles redirection for already logged-in users.
  useEffect(() => {
    const isLoggedIn = localStorage.getItem(LOCAL_STORAGE_KEYS.isLoggedIn);
    const userType = localStorage.getItem(LOCAL_STORAGE_KEYS.userType);

    if (isLoggedIn === "true") {
      const intendedPath = localStorage.getItem(LOCAL_STORAGE_KEYS.intendedVideoPath);
      if (intendedPath) {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.intendedVideoPath);
        navigate(intendedPath, { replace: true });
        return;
      }

      switch (userType) {
        case "user":
          navigate("/", { replace: true });
          break;
        case "admin":
          navigate("/admin/dashboard", { replace: true });
          break;
        default:
          navigate("/", { replace: true });
          break;
      }
    }
  }, [navigate]);

  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);

  // Popup state
  const [popup, setPopup] = useState<{
    open: boolean;
    type: PopupType;
    title: string;
    message: string;
    onClose?: () => void;
  }>({ open: false, type: "notice", title: "", message: "" });

  const validate = () => {
    const digits = phone.replace(/[^\d]/g, '');
    if (digits.length < 8) return 'Enter a valid phone number.';
    if (pin.length < 4 || pin.length > 6 || /\D/.test(pin)) return 'PIN must be 4–6 digits.';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setPopup({ open: true, type: "error", title: "Login Error", message: err });
      return;
    }
    setLoading(true);
    try {
      await sanctum_axios_client.get('/sanctum/csrf-cookie');
      const res = await auth_axios_client.post('/login', {
        phone_number: phone,
        password: pin,
      });

      localStorage.setItem(LOCAL_STORAGE_KEYS.isLoggedIn, "true");
      localStorage.setItem(LOCAL_STORAGE_KEYS.userType, res.data.user.is_admin ? "admin" : "user");

      setPopup({
        open: true,
        type: "success",
        title: "Login Successful",
        message: res.data?.message || "Welcome!",
        onClose: () => {
          setPopup((p) => ({ ...p, open: false }));
          window.location.reload(); // Force reload to trigger redirection
        },
      });
    } catch (e: any) {
      const msg = e?.response?.data?.message || "Something went wrong. Try again.";
      setPopup({ open: true, type: "error", title: "Login Failed", message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login-page">
      <section className="login-card">
        <header
          className="brand"
          onClick={() => navigate('/')}
          role="button"
          tabIndex={0}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            width: '100%',
            marginBottom: '1.5rem'
          }}
        >
          <div className="brand-mark" aria-hidden="true">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6.75 7.5L12 12.75L17.25 7.5L12 2.25L6.75 7.5Z" fill="currentColor"/>
              <path d="M6.75 16.5L12 11.25L17.25 16.5L12 21.75L6.75 16.5Z" fill="currentColor"/>
            </svg>
          </div>
          <h1 className="brand-title" style={{ textAlign: 'center', margin: 0 }}>Hameruha</h1>
        </header>

        <p className="login-prompt">
          Your gateway to expert-led video courses. Sign in to continue.
        </p>

        <form className="login-form" onSubmit={handleSubmit} noValidate>
          <div className="field">
            <label htmlFor="phone">Phone number</label>
            <input
              id="phone"
              name="phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="09XXXXXXXX"
              value={phone}
              maxLength={10}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="pin">PIN</label>
            <div className="pin-group">
              <input
                id="pin"
                name="pin"
                type={showPin ? 'text' : 'password'}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="••••"
                value={pin}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setPin(v);
                }}
              />
              <button
                type="button"
                className="icon-button"
                onClick={() => setShowPin((s) => !s)}
                aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
                aria-pressed={showPin}
              >
                {showPin ? '🙈' : '👁️'}
              </button>
            </div>
            <div className="hint">Use 4–6 digits</div>
          </div>

          <button className="submit" type="submit" disabled={loading}>
            {loading ? <LoadingSpinner /> : 'Sign in'}
          </button>

          <div className="form-footer">
            <a href="#" onClick={(e) => e.preventDefault()}>Forgot PIN?</a>
          </div>
        </form>
      </section>

      <GeneralPopupComponent
        isOpen={popup.open}
        onClose={popup.onClose || (() => setPopup((p) => ({ ...p, open: false })))}
        type={popup.type}
        title={popup.title}
        message={popup.message}
      />
    </main>
  );
};

export default Login;